import type { FunctionComponent, ReactNode } from 'react';
import { useContext } from 'react';

import { Analytics } from '@trello/atlassian-analytics';

import { usePersonalProductivityCohortValue } from 'app/src/components/PersonalProductivityBeta';
import { EnterprisePlanCard } from './PlanCards/EnterprisePlanCard';
import { FreePlanCard } from './PlanCards/FreePlanCard';
import { PremiumPlanCard } from './PlanCards/PremiumPlanCard';
import { StandardPlanCard } from './PlanCards/StandardPlanCard';
import { FeatureCards } from './FeatureCards';
import { PlanComparisonContext } from './PlanComparisonContext';
import type { FeatureItem } from './useFeatures';
import { useFeatures } from './useFeatures';
import { usePersonalProductivityFeatures } from './usePersonalProductivityFeatures';

interface OwnProps {
  isEligibleForTrial: boolean;
  isMonthly: boolean;
  numOfMembers?: number;
  teamId: string;
  teamDisplayName: ReactNode;
  teamName: string;
  onShowFreeTrialModal: (planName: string) => void;
  showRefreshedMessage?: boolean;
}

export const PlanComparisonCards: FunctionComponent<OwnProps> = ({
  isEligibleForTrial,
  isMonthly,
  numOfMembers,
  teamName,
  teamDisplayName,
  teamId,
  onShowFreeTrialModal,
  showRefreshedMessage = false,
}) => {
  const { billingQuote } = useContext(PlanComparisonContext);
  const personalProductivityCohort = usePersonalProductivityCohortValue();
  const isInPPCohort =
    personalProductivityCohort === 'ga' ||
    personalProductivityCohort === 'opted_in';
  const features = useFeatures();
  const personalProductivityFeatures = usePersonalProductivityFeatures();

  const featuresToDisplay = isInPPCohort
    ? personalProductivityFeatures
    : features;

  const sendAnalytics = (name: string) => {
    Analytics.sendClickedButtonEvent({
      buttonName:
        name === 'standard' ? 'upgradeToStandardButton' : 'upgradeToBCButton',
      source: 'planComparisonSection',
      containers: {
        organization: {
          id: teamId,
        },
      },
    });
  };
  return (
    <article>
      <div>
        <FreePlanCard isInPPCohort={isInPPCohort} />
        <StandardPlanCard
          onShowFreeTrialModal={onShowFreeTrialModal}
          isMonthly={isMonthly}
          prices={billingQuote?.standardPrices}
          numOfMembers={numOfMembers}
          teamDisplayName={teamDisplayName}
          teamName={teamName}
          sendAnalytics={sendAnalytics}
          isInPPCohort={isInPPCohort}
        />
        <PremiumPlanCard
          onShowFreeTrialModal={onShowFreeTrialModal}
          isMonthly={isMonthly}
          prices={billingQuote?.businessPrices}
          isEligibleForTrial={isEligibleForTrial}
          numOfMembers={numOfMembers}
          teamDisplayName={teamDisplayName}
          teamName={teamName}
          sendAnalytics={sendAnalytics}
          isInPPCohort={isInPPCohort}
        />
        <EnterprisePlanCard isInPPCohort={isInPPCohort} />
      </div>
      <div>
        {featuresToDisplay.map(
          ({
            key,
            featureName,
            featureIcon,
            free,
            standard,
            business,
            enterprise,
            toolTipTxt,
          }: FeatureItem) => {
            return (
              <FeatureCards
                key={key}
                cardKey={key}
                featureName={featureName}
                featureIcon={featureIcon}
                free={free}
                standard={standard}
                business={business}
                enterprise={enterprise}
                toolTipTxt={toolTipTxt}
              />
            );
          },
        )}
      </div>
    </article>
  );
};
